/**
 * useMediaControls - Hook for managing media tracks
 * Handles mic, camera, and screen share toggles.
 * Mic and camera preferences are persisted to localStorage so they
 * survive page reloads within the same video session.
 */

import { useState, useCallback } from "react";
import { Room, LocalParticipant } from "livekit-client";

const STORAGE_KEY_MIC = "jourfx:video:mic";
const STORAGE_KEY_CAMERA = "jourfx:video:camera";

const STORAGE_KEY_DEVICE_CAMERA = "jourfx:video:device:camera";
const STORAGE_KEY_DEVICE_MIC = "jourfx:video:device:microphone";
const STORAGE_KEY_DEVICE_SPEAKER = "jourfx:video:device:speaker";

function loadBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === "true") return true;
    if (v === "false") return false;
  } catch {}
  return fallback;
}

function saveBool(key: string, value: boolean) {
  try { localStorage.setItem(key, String(value)); } catch {}
}

const DEVICE_STORAGE_KEYS = {
  camera: STORAGE_KEY_DEVICE_CAMERA,
  microphone: STORAGE_KEY_DEVICE_MIC,
  speaker: STORAGE_KEY_DEVICE_SPEAKER,
} as const;

type DeviceKind = keyof typeof DEVICE_STORAGE_KEYS;

/** Save the user's preferred device ID for a given kind. */
export function saveDevicePreference(kind: DeviceKind, deviceId: string) {
  try { localStorage.setItem(DEVICE_STORAGE_KEYS[kind], deviceId); } catch {}
}

/** Load the user's preferred device ID for a given kind. */
export function loadDevicePreference(kind: DeviceKind): string {
  try { return localStorage.getItem(DEVICE_STORAGE_KEYS[kind]) || ""; } catch { return ""; }
}

/**
 * Get the preferred device ID if it's still available, otherwise fall back
 * to the active device or the first in the list.
 */
export function getPreferredDeviceId(
  kind: DeviceKind,
  availableDeviceIds: string[],
  activeDeviceId?: string,
): string {
  const preferred = loadDevicePreference(kind);
  if (preferred && availableDeviceIds.includes(preferred)) return preferred;
  if (activeDeviceId && availableDeviceIds.includes(activeDeviceId)) return activeDeviceId;
  return availableDeviceIds[0] || "";
}

export interface UseMediaControlsOptions {
  room: Room | null;
  /**
   * Optional custom camera strategy. If provided, this is used instead of
   * localParticipant.setCameraEnabled() so app-level flows can publish/
   * unpublish camera tracks explicitly (e.g. with pre-applied processors).
   */
  onToggleCamera?: (
    desiredState: boolean,
    localParticipant: LocalParticipant
  ) => Promise<void>;
}

export interface UseMediaControlsReturn {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenSharing: boolean;
  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  microphoneError: Error | null;
  cameraError: Error | null;
  screenShareError: Error | null;
}

export function useMediaControls(
  options: UseMediaControlsOptions
): UseMediaControlsReturn {
  const { room, onToggleCamera } = options;

  const [isMicEnabled, setIsMicEnabled] = useState(() => loadBool(STORAGE_KEY_MIC, true));
  const [isCameraEnabled, setIsCameraEnabled] = useState(() => loadBool(STORAGE_KEY_CAMERA, true));
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [microphoneError, setMicrophoneError] = useState<Error | null>(null);
  const [cameraError, setCameraError] = useState<Error | null>(null);
  const [screenShareError, setScreenShareError] = useState<Error | null>(null);

  const toggleMic = useCallback(async () => {
    if (!room?.localParticipant) return;

    try {
      setMicrophoneError(null);
      const newState = !isMicEnabled;
      await room.localParticipant.setMicrophoneEnabled(newState);
      setIsMicEnabled(newState);
      saveBool(STORAGE_KEY_MIC, newState);
    } catch (err) {
      setMicrophoneError(
        err instanceof Error ? err : new Error("Microphone error")
      );
    }
  }, [room, isMicEnabled]);

  const toggleCamera = useCallback(async () => {
    if (!room?.localParticipant) return;

    try {
      setCameraError(null);
      const newState = !isCameraEnabled;
      if (onToggleCamera) {
        await onToggleCamera(newState, room.localParticipant);
      } else {
        await room.localParticipant.setCameraEnabled(newState);
      }
      setIsCameraEnabled(newState);
      saveBool(STORAGE_KEY_CAMERA, newState);
    } catch (err) {
      setCameraError(err instanceof Error ? err : new Error("Camera error"));
    }
  }, [room, isCameraEnabled, onToggleCamera]);

  const toggleScreenShare = useCallback(async () => {
    if (!room?.localParticipant) return;

    try {
      setScreenShareError(null);
      const newState = !isScreenSharing;
      await room.localParticipant.setScreenShareEnabled(newState);
      setIsScreenSharing(newState);
    } catch (err) {
      setScreenShareError(
        err instanceof Error ? err : new Error("Screen share error")
      );
    }
  }, [room, isScreenSharing]);

  return {
    isMicEnabled,
    isCameraEnabled,
    isScreenSharing,
    toggleMic,
    toggleCamera,
    toggleScreenShare,
    microphoneError,
    cameraError,
    screenShareError,
  };
}
